import {FC, useCallback, useMemo} from 'react';
import {getProcessActionsPopupMessage, ProcessActions} from '../../../constants/constants';
import permissionsInfo from '../../../constants/permissionsInfo';
import {Assignee, GroupListProps} from '../../../constants/types';
import {currentUserProps} from '../../../store/currentUser/currentUser.slice';
import {useAppSelector} from '../../../store/hooks';
import {RootState} from '../../../store/store';
import EnsurePermission from '../../common/EnsurePermission';
import ListOptions from '../../common/ListOptions';
import {CurrentProcess} from '../../tasks/types';
import {getProcessClaimingButtonOptions, shouldShowProcessClaimingButton} from '../helpers';
import {ProcessActionPopupData} from '../types';
import {useTranslation} from 'react-i18next';

type ProcessAssigneeElementProps = {
  assignees: Assignee[],
  processId: number,
  processData: Nullable<CurrentProcess>,
  setProcessActionsPopupData: (value: Nullable<ProcessActionPopupData>) => void,
  availableProcessAssigneeGroups: Nullable<GroupListProps[]>
}

const ProcessAssigneeElement: FC<ProcessAssigneeElementProps> = ({
  assignees, processId, processData,
  setProcessActionsPopupData, availableProcessAssigneeGroups
}) => {
  const {t} = useTranslation('processes');
  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );

  const {id: currentUserId, groups: userGroups = []} = currentUser || {};

  const hasProcessClaimButton = useMemo(() =>
    shouldShowProcessClaimingButton(
        {availableProcessAssigneeGroups, userGroups}
    ), [availableProcessAssigneeGroups, userGroups]);

  const handleShowProcessActionsPopup = useCallback((
      id: number,
      processActionsType: ProcessActions,
      confirmBtnLabel: string,
      assigneeId: number
  ) => {
    const message = getProcessActionsPopupMessage(processActionsType);
    setProcessActionsPopupData(
        {
          id,
          type: processActionsType,
          message,
          confirmBtn: confirmBtnLabel,
          assigneeId
        }
    );
  }, []);

  const {
    mainOption: {
      name: mainOptionName,
      onClick: mainOptionOnClick,
      optionElementOnClick
    },
    otherOptions,
    showMainOptionButton,
    processActionType
  } =
    getProcessClaimingButtonOptions(assignees, currentUserId);

  const options = useMemo(() => {
    const mainOptionData = {
      MAIN_PROCESS_ACTION: {
        name: mainOptionName,
        onClick: () =>
          mainOptionOnClick(handleShowProcessActionsPopup, processId)
      }
    };

    if (!otherOptions.length || processActionType === ProcessActions.DISCARD) {
      return mainOptionData;
    }

    const otherOptionsData = otherOptions.reduce((
        obj, {name, assigneeId, disabled}, id
    ) => {
      const objectKeyName = `OTHER_PROCESS_ACTION_${id}`;

      return {
        ...obj,
        [objectKeyName]: {
          name: name,
          onClick: () => optionElementOnClick(
              handleShowProcessActionsPopup, processId, assigneeId
          ),
          disabled: disabled
        }
      };
    }, {});

    if (showMainOptionButton) {
      return {...mainOptionData, ...otherOptionsData};
    } else {
      return {...otherOptionsData};
    }
  }, [processData, assignees]);

  return (
    <div className="process-assignee">
      <div className="process-assignee_wrapper">
        <span className="process-assignee-title">{t('processDetailsLabels.processAssignee')}</span>
        {assignees.map((assignee) => (
          (<span className="process-assignee-value" key={assignee.id}>{`${assignee.firstName} ${assignee.lastName}`}</span>)
        ))}
      </div>
      <EnsurePermission elementPermissions={permissionsInfo.tasks.claim}>
        {hasProcessClaimButton && (
          <ListOptions
            options={options}
            position="vertical"
            size="small"
          />
        )}
      </EnsurePermission>
    </div>

  );
};

export default ProcessAssigneeElement;
